import {queryOptions} from '@tanstack/react-query'
import {notFound} from '@tanstack/react-router'
import {createServerFn} from '@tanstack/react-start'
import {z} from 'zod'
import {
    getCompositionImageBaseUrl,
    getDesignImageSrc,
    getDesignImageSrcSet,
    getProductDefaultAppearanceId,
    getProductDefaultViewId,
    getVirtualProductId,
    parseProductId,
} from '~/utils/product'
import fetchProducts from '~/client/spreadshop/old/fetchProducts'
import {ProductWithIds} from '~/client/spreadshop/missing-types'
import {shuffleBySeed} from '~/utils/random'
import {Locale, Maybe} from '~/gql/graphql'

const CACHE_TIME_SEC = 60 * 60 * 3 // 3h
const SEED = 1734

const collectionProductsPreview = z.object({
    collectionId: z.string(),
    locale: z.nativeEnum(Locale).optional(),
})

const getCollectionProductsPreview = createServerFn({
    method: 'GET',
})
    .inputValidator((data: unknown) => {
        const result = collectionProductsPreview.safeParse(data)
        if (result.error) {
            throw notFound()
        }
        return result.data
    })
    .handler(async ({data}) => {
        const {collectionId, locale} = data

        const response = await fetchProducts(0, locale, {
            collectionId,
        })

        const shuffledProducts = shuffleBySeed(response.products?.items ?? [], SEED)

        const products: Maybe<ProductWithIds[]> = shuffledProducts.map((product) => {
            const {ideaId, sellableId} = parseProductId(product?.id!)

            const imageSrc = product?.image?.src
            const baseDesignImageSrc = getCompositionImageBaseUrl(imageSrc)!

            const defaultAppearanceId = getProductDefaultAppearanceId(imageSrc)
            const defaultViewId = getProductDefaultViewId(imageSrc) ?? '1'

            return {
                ...product,
                designImage: {
                    src: getDesignImageSrc(baseDesignImageSrc, defaultViewId, defaultAppearanceId),
                    srcset: getDesignImageSrcSet(
                        baseDesignImageSrc,
                        defaultViewId,
                        defaultAppearanceId
                    ),
                    altText: product?.image?.altText,
                },
                defaultAppearanceId: defaultAppearanceId,
                defaultViewId: defaultViewId,
                vpId: getVirtualProductId(imageSrc),
                ideaId,
                sellableId,
            }
        })

        return products.slice(0, 16)
    })

export const collectionProductsPreviewQueryOptions = (
    collectionId: string,
    locale: Locale = Locale.EnEu
) =>
    queryOptions({
        queryKey: [locale, 'collections', collectionId, 'products-preview'],
        queryFn: () => getCollectionProductsPreview({data: {collectionId, locale}}),
        gcTime: 1000 * CACHE_TIME_SEC, // 3h
        staleTime: 1000 * CACHE_TIME_SEC, // 3h
    })
